import type {
  BookingInspection,
  BookingInspectionActor,
  BookingInspectionPhase,
  BookingInspectionReadingType,
  BookingInspectionSummary,
  Nullable,
} from '~/types'

/**
 * Returns the completed photos of the given inspections. Photos still pending,
 * retrying or failed are left out.
 *
 * @param inspections The inspections to get the photos for.
 * @returns The inspection photos.
 *
 * @example getInspectionPhotos([{ photos: [{ status: 'Completed', url: 'https://example.com/photo.jpg' }] }]) // [{ type: 'image', path: 'https://example.com/photo.jpg', src: 'https://example.com/photo.jpg', description: '', width: 450, height: 300 }]
 */
export function getInspectionPhotos(inspections?: Nullable<BookingInspection[]>) {
  if (!inspections) {
    return []
  }

  return inspections
    .flatMap((inspection) => inspection.photos ?? [])
    .filter((photo) => photo.status === 'Completed' && Boolean(photo.url))
    .map((photo) => {
      return {
        type: 'image' as const,
        path: photo.url!,
        src: photo.url!,
        description: '',
        width: 450,
        height: 300,
      }
    })
}

/**
 * Returns the recorded value of one reading type on an inspection summary.
 *
 * @param inspection The inspection summary to read from.
 * @param type The reading type to find.
 * @returns The reading value, or undefined when it was not recorded.
 *
 * @example getInspectionReading({ readings: [{ type: 'Odometer', readingValue: 1200 }] }, 'Odometer') // 1200
 */
export function getInspectionReading(inspection: Nullable<BookingInspectionSummary> | undefined, type: BookingInspectionReadingType) {
  return inspection?.readings?.find((reading) => reading.type === type)?.readingValue
}

/**
 * Returns the started inspection summary for one booking side and phase. The
 * host's and guest's inspections share one list, so both are matched.
 *
 * @param inspections The booking's inspection summaries.
 * @param actor Whose inspection to find: Owner for the host, Renter for the guest.
 * @param phase The inspection phase to find.
 * @returns The inspection summary, or null when that side has not started it.
 *
 * @example findStartedInspection([{ actor: 'Owner', phase: 'Departure', state: 'Complete' }], 'Owner', 'Departure') // { actor: 'Owner', phase: 'Departure', state: 'Complete' }
 */
export function findStartedInspection(inspections: BookingInspectionSummary[], actor: BookingInspectionActor, phase: BookingInspectionPhase) {
  return inspections.find((inspection) => inspection.actor === actor && inspection.phase === phase && inspection.state !== 'NotStarted') ?? null
}
